import { useEffect, useRef, useState, useMemo } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import _ from "lodash";
import { InputAdornment, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";
import {
  getCategoryFromTypes,
  getPlaceCategoryIcon,
  Category,
} from "@/utils/placeFilters";
import useApiClient from "@/api/Client";
import ENDPOINTS from "@/api/endpoints";
import { toast } from "react-toastify";
import { GooglePlacePrediction, Coordinates, Place } from "@/types/common";
import { getApiErrorMessage } from "@/utils/helper";

const styles = {
  searchField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: "62px",
      fontSize: "16px",
      color: "#626262",
      boxShadow: "0px 1px 5px 0px #00000040",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "& input::placeholder": {
        color: "#626262",
        fontSize: { xs: "14px", sm: "16px" },
        opacity: 1,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: { xs: "4.2% 2%", sm: "10px 14px" },
      color: "#626262",
    },
  },
};

export default function PlaceAutocomplete({
  onSelect,
  userLocation,
}: {
  onSelect: (place: Place | null) => void;
  userLocation:
    | Coordinates
    | { coords: { latitude: number; longitude: number } }
    | null
    | undefined;
}) {
  const { api } = useApiClient();
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<GooglePlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get(ENDPOINTS.CATEGORIES.GET_ALL);
        setCategories(res?.data?.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, [api]);

  const sessionToken = useRef<string>(crypto.randomUUID());

  const debouncedFetch = useMemo(
    () =>
      _.debounce(
        async (
          input: string,
          location:
            | Coordinates
            | { coords: { latitude: number; longitude: number } }
            | null
            | undefined,
        ) => {
          if (input.length < 3) return;

          if (!sessionToken.current) {
            sessionToken.current = crypto.randomUUID();
          }

          try {
            // Handle userLocation which might be a Geolocation Position object or { lat, lng }
            let center = undefined;
            if (location) {
              if (location && "coords" in location) {
                const { latitude, longitude } = location.coords;
                if (latitude !== 0 || longitude !== 0) {
                  center = { lat: latitude, lng: longitude };
                }
              } else if (location && "lat" in location && "lng" in location) {
                if (location.lat !== 0 || location.lng !== 0) {
                  center = location;
                }
              }
            }

            const request = {
              input,
              sessionToken: sessionToken.current,
              includedRegionCodes: ["in"], // Restrict to India
              includedPrimaryTypes: ["establishment"],
              locationBias: center
                ? {
                    center: center,
                    radius: 10000, // 10km
                  }
                : undefined,
            };

            const res = await api.post(
              ENDPOINTS.PLACES.GOOGLE_AUTOCOMPLETE,
              request,
            );
            const suggestions = res.data?.data || [];

            setPredictions(suggestions);
          } catch (err: unknown) {
            toast.error(getApiErrorMessage(err, "Failed to fetch places"));
          } finally {
            setLoading(false);
          }
        },
        400,
      ),
    [api],
  );

  useEffect(() => {
    if (query.length >= 3) {
      setLoading(true);
      debouncedFetch(query, userLocation);
    } else {
      setPredictions([]);
      setLoading(false);
    }
  }, [query, debouncedFetch, userLocation]);

  const handleSelectPrediction = async (
    suggestion: GooglePlacePrediction | null,
  ) => {
    try {
      if (!suggestion) {
        onSelect(null);
        return;
      }

      if (typeof suggestion === "string" || !suggestion.placePrediction) {
        return;
      }

      const placeId = suggestion.placePrediction.placeId;

      const res = await api.get(ENDPOINTS.PLACES.GOOGLE_DETAILS(placeId), {
        fields: [
          "id",
          "displayName",
          "formattedAddress",
          "location",
          "types",
          "addressComponents",
        ].join(","),
        sessionToken: sessionToken.current,
      });

      const place = res.data?.data;

      if (!place) return;

      const { category_id, category, subcategory_id, subcategory } =
        place.types_mapped || {
          category_id: null,
          category: "Other",
          subcategory_id: null,
          subcategory: "Other",
        };

      const { country_code, city, postcode } =
        place.address_components_mapped || {
          country_code: "",
          city: "",
          postcode: "",
        };

      onSelect({
        g_place_id: place.id,
        name: place.displayName?.text || place.displayName,
        address_text: place.formattedAddress,
        coordinates: {
          lat: place.location?.latitude || 0,
          lng: place.location?.longitude || 0,
        },
        country_code,
        city,
        postcode,
        category_id,
        category,
        subcategory_id,
        subcategory,
      });

      // Close the keyboard
      (document.activeElement as HTMLElement)?.blur();

      setQuery(place.displayName?.text || place.displayName || "");
      setPredictions([]);
      sessionToken.current = crypto.randomUUID();
    } catch (err) {
      console.error("Selection Error:", err);
    }
  };

  return (
    <Autocomplete
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "50px",
          backgroundColor: "#fff",
          boxShadow: "0px 1px 5px 0px #00000040",
          "&:hover fieldset": {
            borderColor: "#7747FB",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#7747FB",
            borderWidth: 2,
          },
        },
      }}
      slotProps={{
        paper: {
          sx: {
            position: "fixed",
            left: "50%",
            transform: "translateX(-50%)",
            top: "5px",
            width: "98vw",
            maxWidth: "98vw",
            borderRadius: "20px",
            boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
            zIndex: 9999,
          },
        },
      }}
      fullWidth
      forcePopupIcon={false}
      getOptionLabel={(option: GooglePlacePrediction | string) =>
        typeof option === "string"
          ? option
          : option.placePrediction?.text?.text || ""
      }
      filterOptions={(x) => x}
      options={predictions}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={null}
      noOptionsText={
        <Typography sx={{ color: "#313131", textAlign: "center", py: 1 }}>
          {query.length < 3
            ? "Type at least 3 characters..."
            : loading
            ? "Searching..."
            : "Location not found"}
        </Typography>
      }
      onChange={(_, newValue: GooglePlacePrediction | string | null) => {
        handleSelectPrediction(newValue as GooglePlacePrediction | null);
      }}
      onInputChange={(_, newInputValue) => {
        setQuery(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          fullWidth
          variant="outlined"
          sx={styles.searchField}
          placeholder="Search for places"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="start">
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="end">
                <Search
                  sx={{
                    color: "#313131",
                    fontSize: { xs: "20px", sm: "20px" },
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(props, option: GooglePlacePrediction) => {
        const { key, ...optionProps } = props;
        const types = option.placePrediction?.types;
        const { category, subcategory } = getCategoryFromTypes(
          types || [],
          categories,
        );
        const IconComponent = getPlaceCategoryIcon(category, subcategory);

        return (
          <li key={key} {...optionProps}>
            <Grid
              container
              sx={{ justifyContent: "space-between", alignItems: "center" }}
            >
              <Grid size={{ xs: 0.8 }} sx={{ textAlign: "center" }}>
                <IconComponent sx={{ color: "gray", fontSize: 18 }} />
              </Grid>
              <Grid size={{ xs: 10.9 }}>
                <Typography sx={{ fontSize: { xs: "3.4vw", sm: 14 } }}>
                  {option.placePrediction?.text?.text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
